// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Crew Conversation System
 * 
 * Defines conversation templates that appear in logs based on:
 * - Crew relationships
 * - Recent events
 * - Personality traits
 * - Ship status
 */

// Conversation triggers
export const CONVERSATION_TRIGGERS = {
  RELATIONSHIP_HIGH: 'relationship_high',
  RELATIONSHIP_LOW: 'relationship_low',
  AFTER_COMBAT: 'after_combat',
  AFTER_TRADE: 'after_trade',
  LOW_MORALE: 'low_morale',
  HIGH_MORALE: 'high_morale',
  NEW_CREW: 'new_crew',
  INJURY: 'injury',
  RANDOM: 'random'
};

// Conversation templates
export const CONVERSATION_TEMPLATES = {
  [CONVERSATION_TRIGGERS.RELATIONSHIP_HIGH]: [
    {
      id: 'friendship_bond',
      participants: 2,
      relationshipEffect: 5,
      templates: {
        social: {
          speaker1: "I'm really glad we're serving together, {name2}.",
          speaker2: "Same here, {name1}. You always know how to lighten the mood.",
          context: "The two crew members share a moment of camaraderie."
        },
        technical: {
          speaker1: "Your work on the {system} has been exceptional, {name2}.",
          speaker2: "Thanks {name1}. Your attention to detail makes my job easier.",
          context: "Professional respect deepens their friendship."
        },
        brave: {
          speaker1: "I know I can count on you in a tight spot, {name2}.",
          speaker2: "Always, {name1}. We've got each other's backs.",
          context: "Their shared courage strengthens their bond."
        }
      }
    },
    {
      id: 'shared_memory',
      participants: 2,
      relationshipEffect: 4,
      templates: {
        optimistic: {
          speaker1: "Remember when we thought we wouldn't make it past that nebula? Look at us now.",
          speaker2: "Ha! We've come a long way since then, {name1}.",
          context: "Fond memories strengthen their bond."
        },
        creative: {
          speaker1: "I sketched the view from the observation deck last night. Thought you'd want a copy, {name2}.",
          speaker2: "That's... really thoughtful, {name1}. I'll hang it in my quarters.",
          context: "A creative gesture deepens their friendship."
        },
        introverted: {
          speaker1: "I don't say this often, but... I'm glad you're here, {name2}.",
          speaker2: "That means a lot coming from you, {name1}.",
          context: "A rare moment of openness from a reserved crew member."
        },
        pessimistic: {
          speaker1: "If things ever go sideways out here, I want you to know — you made this trip worth it, {name2}.",
          speaker2: "Don't talk like that, {name1}. We're going to be fine. Together.",
          context: "Even pessimism can't overshadow genuine connection."
        }
      }
    },
    {
      id: 'trust_deepens',
      participants: 2,
      relationshipEffect: 6,
      templates: {
        cautious: {
          speaker1: "I don't trust easily, {name2}. But I trust you with my life.",
          speaker2: "That's the highest compliment you could give, {name1}.",
          context: "A cautious soul lets their guard down."
        },
        social: {
          speaker1: "The crew's been asking if we're related — we finish each other's sentences now, {name2}.",
          speaker2: "Ha! Might as well be family at this point.",
          context: "Their closeness is noticed by the whole crew."
        }
      }
    }
  ],

  [CONVERSATION_TRIGGERS.RELATIONSHIP_LOW]: [
    {
      id: 'tension_conflict',
      participants: 2,
      relationshipEffect: -3,
      templates: {
        pessimistic: {
          speaker1: "This mission is going nowhere fast.",
          speaker2: "Maybe if you actually helped instead of complaining, {name1}.",
          context: "Tension flares between the crew members."
        },
        cautious: {
          speaker1: "I don't like how reckless we've been lately.",
          speaker2: "Sometimes you have to take risks, {name1}.",
          context: "Different approaches to danger create friction."
        }
      }
    },
    {
      id: 'cold_shoulder',
      participants: 2,
      relationshipEffect: -2,
      templates: {
        introverted: {
          speaker1: "...",
          speaker2: "Fine. Don't talk to me then, {name1}.",
          context: "Silence speaks louder than words."
        },
        technical: {
          speaker1: "You cross-wired the {system} again. I had to redo the whole thing.",
          speaker2: "Maybe if your documentation wasn't incomprehensible, {name1}.",
          context: "Professional disagreements turn personal."
        },
        brave: {
          speaker1: "You froze up back there, {name2}. People could have died.",
          speaker2: "Not everyone charges in without thinking, {name1}.",
          context: "A clash over courage and caution."
        },
        social: {
          speaker1: "I tried to include you, {name2}. You shut me out every time.",
          speaker2: "Maybe I just don't want to be your project, {name1}.",
          context: "Good intentions meet resistance."
        }
      }
    },
    {
      id: 'simmering_resentment',
      participants: 2,
      relationshipEffect: -4,
      templates: {
        optimistic: {
          speaker1: "I keep trying to see the best in you, {name2}, but you make it really hard.",
          speaker2: "I never asked for your approval.",
          context: "Even optimism has its limits."
        },
        creative: {
          speaker1: "You have zero appreciation for anything that isn't strictly by the book.",
          speaker2: "And you treat everything like a game, {name1}. This is life and death.",
          context: "Fundamentally different worldviews collide."
        }
      }
    }
  ],

  [CONVERSATION_TRIGGERS.AFTER_COMBAT]: [
    {
      id: 'combat_debrief',
      participants: 2,
      relationshipEffect: 3,
      templates: {
        brave: {
          speaker1: "That was some fine shooting out there, {name2}!",
          speaker2: "You weren't bad yourself, {name1}. Good teamwork.",
          context: "Combat brings out mutual respect."
        },
        cautious: {
          speaker1: "We were lucky to make it through that one.",
          speaker2: "Luck had nothing to do with it. We were prepared.",
          context: "Different perspectives on their survival."
        }
      }
    },
    {
      id: 'adrenaline_crash',
      participants: 2,
      relationshipEffect: 2,
      templates: {
        pessimistic: {
          speaker1: "One of these days our luck is going to run out, {name2}.",
          speaker2: "Then we'd better make today count.",
          context: "Post-combat reality sets in."
        },
        social: {
          speaker1: "Everyone okay? {name2}, you took a hit back there.",
          speaker2: "Just a scratch. Thanks for checking, {name1}.",
          context: "Concern for each other after the fight."
        },
        technical: {
          speaker1: "The {system} held up better than I expected. My modifications worked.",
          speaker2: "Remind me to buy you a drink when we dock, {name1}.",
          context: "Technical skill proved its worth in combat."
        },
        optimistic: {
          speaker1: "We're still here! That's what matters, {name2}.",
          speaker2: "You always find the silver lining, don't you, {name1}?",
          context: "Relief and gratitude after surviving."
        }
      }
    },
    {
      id: 'combat_tension',
      participants: 2,
      relationshipEffect: -1,
      templates: {
        introverted: {
          speaker1: "I need some time alone after that.",
          speaker2: "We should be debriefing, not hiding in our quarters, {name1}.",
          context: "Different ways of processing combat stress."
        },
        creative: {
          speaker1: "There has to be a better way than just shooting at everything.",
          speaker2: "Feel free to negotiate with the next volley of torpedoes, {name1}.",
          context: "Idealism clashes with pragmatism."
        }
      }
    }
  ],

  [CONVERSATION_TRIGGERS.AFTER_TRADE]: [
    {
      id: 'trade_review',
      participants: 2,
      relationshipEffect: 2,
      templates: {
        technical: {
          speaker1: "Those spare {system} parts were a good find. Fair price too.",
          speaker2: "I ran the numbers — we came out ahead by at least 15%.",
          context: "Satisfaction over a successful trade."
        },
        social: {
          speaker1: "The merchants here are friendly. I got us invited to a local gathering tonight.",
          speaker2: "Only you could turn a supply run into a social event, {name1}.",
          context: "Making connections wherever they go."
        },
        cautious: {
          speaker1: "I double-checked the cargo manifest. Everything's accounted for.",
          speaker2: "Good. Last port we were shorted two crates.",
          context: "Vigilance pays off in trade."
        },
        creative: {
          speaker1: "Did you see the artisan market? I picked up something for the common room.",
          speaker2: "As long as it doesn't take up cargo space, {name1}.",
          context: "Finding beauty even on supply runs."
        }
      }
    },
    {
      id: 'trade_disagreement',
      participants: 2,
      relationshipEffect: -1,
      templates: {
        pessimistic: {
          speaker1: "We overpaid. I've seen those goods for half the price at Omega Station.",
          speaker2: "We needed them now, not three jumps from now, {name1}.",
          context: "Second-guessing trade decisions."
        },
        optimistic: {
          speaker1: "Think of it as investing in a relationship with this port, {name2}.",
          speaker2: "I'd rather invest in not going broke.",
          context: "Different philosophies on spending."
        }
      }
    }
  ],

  [CONVERSATION_TRIGGERS.NEW_CREW]: [
    {
      id: 'welcome_newcomer',
      participants: 2,
      relationshipEffect: 2,
      templates: {
        social: {
          speaker1: "Welcome aboard! I'm {name1}. Let me show you around.",
          speaker2: "Thanks, I appreciate it. This ship is impressive.",
          context: "A warm welcome for the new crew member."
        },
        introverted: {
          speaker1: "You'll find your quarters down the hall.",
          speaker2: "Thanks. I'll try to stay out of everyone's way.",
          context: "A more reserved introduction."
        }
      }
    },
    {
      id: 'sizing_up',
      participants: 2,
      relationshipEffect: 1,
      templates: {
        brave: {
          speaker1: "So, new blood. Ever been in a real firefight?",
          speaker2: "A few. I can handle myself.",
          context: "Testing the newcomer's mettle."
        },
        technical: {
          speaker1: "What's your specialty? We could use help with the {system}.",
          speaker2: "I've worked on similar systems before. Show me what you've got.",
          context: "Assessing the newcomer's skills."
        },
        cautious: {
          speaker1: "Just so you know — we follow protocols on this ship. No cowboy stuff.",
          speaker2: "Understood. I'm here to do the job right.",
          context: "Setting expectations early."
        },
        optimistic: {
          speaker1: "You're going to love it here, {name2}. Best crew in the sector.",
          speaker2: "That's what I've heard. Glad to be part of it.",
          context: "An enthusiastic welcome."
        },
        pessimistic: {
          speaker1: "Hope you last longer than the last one.",
          speaker2: "...thanks for the warm welcome, {name1}.",
          context: "A less-than-encouraging greeting."
        },
        creative: {
          speaker1: "We do movie nights on Thursdays. You in, {name2}?",
          speaker2: "Absolutely. What's the current pick?",
          context: "Bonding over shared interests right away."
        }
      }
    }
  ],

  [CONVERSATION_TRIGGERS.INJURY]: [
    {
      id: 'injury_concern',
      participants: 2,
      relationshipEffect: 4,
      templates: {
        social: {
          speaker1: "I heard you got hurt. Are you okay, {name2}?",
          speaker2: "I'll live. Thanks for asking, {name1}.",
          context: "Genuine concern for an injured crewmate."
        },
        brave: {
          speaker1: "That was a nasty hit, {name2}. You took it like a champ though.",
          speaker2: "Didn't have much choice. Just glad the {system} held together.",
          context: "Admiration for toughness under fire."
        },
        technical: {
          speaker1: "I've recalibrated the {system} so that shouldn't happen again, {name2}.",
          speaker2: "Appreciate it, {name1}. Rather not repeat that experience.",
          context: "Fixing the problem to prevent future injuries."
        },
        cautious: {
          speaker1: "This is exactly what I warned about. We need better safety protocols.",
          speaker2: "You're not wrong, {name1}. Let's draft something up.",
          context: "An injury validates cautious concerns."
        }
      }
    },
    {
      id: 'injury_guilt',
      participants: 2,
      relationshipEffect: 3,
      templates: {
        pessimistic: {
          speaker1: "I should have seen it coming. I'm sorry, {name2}.",
          speaker2: "It's not your fault. Nobody could have predicted that.",
          context: "Self-blame after a crewmate's injury."
        },
        optimistic: {
          speaker1: "Hey, the doc says you'll be back on your feet in no time, {name2}.",
          speaker2: "Easy for you to say from over there, {name1}.",
          context: "Trying to keep spirits up despite the pain."
        },
        introverted: {
          speaker1: "*leaves a get-well note and some snacks outside {name2}'s quarters*",
          speaker2: "...that was really kind. I'll have to thank {name1} later.",
          context: "A quiet gesture says more than words."
        },
        creative: {
          speaker1: "I made you something to pass the time while you recover, {name2}.",
          speaker2: "A puzzle box? {name1}, this is incredible. Thank you.",
          context: "A handmade gift to aid recovery."
        }
      }
    }
  ],

  [CONVERSATION_TRIGGERS.LOW_MORALE]: [
    {
      id: 'morale_support',
      participants: 2,
      relationshipEffect: 4,
      templates: {
        optimistic: {
          speaker1: "Hey {name2}, things will turn around. They always do.",
          speaker2: "I hope you're right, {name1}. Thanks for saying that.",
          context: "Encouragement during difficult times."
        },
        social: {
          speaker1: "Want to grab some synth-coffee and talk?",
          speaker2: "That sounds good, {name1}. I could use the company.",
          context: "Offering companionship during low spirits."
        }
      }
    },
    {
      id: 'morale_coping',
      participants: 2,
      relationshipEffect: 2,
      templates: {
        creative: {
          speaker1: "I've been writing about everything we've been through. Helps me process it.",
          speaker2: "Maybe I should try that. Beats staring at the ceiling, {name1}.",
          context: "Finding creative outlets for stress."
        },
        technical: {
          speaker1: "I've been burying myself in {system} diagnostics. Keeps my mind off things.",
          speaker2: "Whatever works, {name1}. Just don't burn yourself out.",
          context: "Coping through work."
        },
        brave: {
          speaker1: "We've survived worse than this, {name2}. We'll get through it.",
          speaker2: "I know. Just... tired, you know?",
          context: "Courage meets exhaustion."
        },
        introverted: {
          speaker1: "I found a quiet spot near the observation deck. You're welcome to join me, {name2}. No talking required.",
          speaker2: "...that actually sounds perfect right now.",
          context: "Comfort in shared silence."
        }
      }
    },
    {
      id: 'morale_frustration',
      participants: 2,
      relationshipEffect: -2,
      templates: {
        pessimistic: {
          speaker1: "What's even the point anymore, {name2}?",
          speaker2: "Don't start with that. We need everyone focused.",
          context: "Despair threatens to spread."
        },
        cautious: {
          speaker1: "We should turn back. This isn't worth the risk.",
          speaker2: "We've come too far to quit now, {name1}.",
          context: "Fear and determination clash."
        }
      }
    }
  ],

  [CONVERSATION_TRIGGERS.HIGH_MORALE]: [
    {
      id: 'celebration',
      participants: 2,
      relationshipEffect: 3,
      templates: {
        social: {
          speaker1: "This calls for a celebration! Who's up for a party in the mess hall?",
          speaker2: "Count me in, {name1}! I'll bring the music.",
          context: "High spirits lead to crew bonding."
        },
        optimistic: {
          speaker1: "See? I told you things would work out, {name2}!",
          speaker2: "Alright, alright — you were right this time, {name1}.",
          context: "Vindicated optimism."
        },
        brave: {
          speaker1: "This crew can handle anything the galaxy throws at us.",
          speaker2: "After what we've been through? Absolutely, {name1}.",
          context: "Confidence born from shared success."
        },
        creative: {
          speaker1: "I'm composing something to commemorate this moment, {name2}.",
          speaker2: "The Ballad of the Starship? I love it.",
          context: "Immortalizing good times through art."
        }
      }
    },
    {
      id: 'good_vibes',
      participants: 2,
      relationshipEffect: 2,
      templates: {
        technical: {
          speaker1: "Everything's running at peak efficiency. Even the {system} is purring.",
          speaker2: "When the ship's happy, I'm happy, {name1}.",
          context: "Good morale reflected in ship performance."
        },
        introverted: {
          speaker1: "It's... nice. When everyone's like this.",
          speaker2: "Yeah. It is, {name1}.",
          context: "Even the quiet ones feel the good energy."
        },
        cautious: {
          speaker1: "Let's not get complacent. But... yeah. This is good.",
          speaker2: "Did {name1} just admit things are going well? Mark the date!",
          context: "Even the cautious one can't deny the good times."
        },
        pessimistic: {
          speaker1: "I keep waiting for the other shoe to drop.",
          speaker2: "For once, {name1}, just enjoy the moment.",
          context: "Struggling to accept that things are actually good."
        }
      }
    }
  ],

  [CONVERSATION_TRIGGERS.RANDOM]: [
    {
      id: 'daily_chat',
      participants: 2,
      relationshipEffect: 1,
      templates: {
        technical: {
          speaker1: "The {system} efficiency is up 3% this cycle.",
          speaker2: "Nice work on the calibrations, {name1}.",
          context: "Shop talk during routine duties."
        },
        creative: {
          speaker1: "I've been working on some music in my spare time.",
          speaker2: "I'd love to hear it sometime, {name1}.",
          context: "Sharing personal interests."
        }
      }
    },
    {
      id: 'mess_hall',
      participants: 2,
      relationshipEffect: 1,
      templates: {
        social: {
          speaker1: "Saved you a seat, {name2}. The synth-steak is actually decent today.",
          speaker2: "Decent? That's high praise from you, {name1}.",
          context: "Casual meal together."
        },
        pessimistic: {
          speaker1: "Another day of recycled protein paste. Living the dream.",
          speaker2: "At least it's warm this time, {name1}.",
          context: "Bonding over bad food."
        },
        optimistic: {
          speaker1: "I heard the next port has real food. Actual vegetables, {name2}!",
          speaker2: "Don't tease me, {name1}. I've been dreaming about fresh fruit.",
          context: "Something to look forward to."
        }
      }
    },
    {
      id: 'downtime',
      participants: 2,
      relationshipEffect: 1,
      templates: {
        brave: {
          speaker1: "Want to hit the training sim, {name2}? I've programmed a new scenario.",
          speaker2: "You're on. Loser buys drinks at the next port.",
          context: "Competitive training during downtime."
        },
        introverted: {
          speaker1: "Mind if I read in here? I won't bother you.",
          speaker2: "Go ahead, {name1}. The quiet company is nice.",
          context: "Comfortable silence between crewmates."
        },
        cautious: {
          speaker1: "I've been reviewing the emergency procedures. Want to run through them, {name2}?",
          speaker2: "Again? ...fine. Better safe than sorry.",
          context: "Preparedness as a bonding activity."
        }
      }
    },
    {
      id: 'stargazing',
      participants: 2,
      relationshipEffect: 2,
      templates: {
        creative: {
          speaker1: "Look at that nebula, {name2}. The colors are unreal.",
          speaker2: "Sometimes I forget how beautiful it is out here.",
          context: "Sharing a quiet moment of wonder."
        },
        technical: {
          speaker1: "That star cluster is emitting some unusual readings. Fascinating.",
          speaker2: "Only you would call anomalous radiation 'fascinating,' {name1}.",
          context: "Different ways of appreciating the cosmos."
        },
        optimistic: {
          speaker1: "Think about it — we're seeing things no one back home ever will, {name2}.",
          speaker2: "When you put it that way... yeah. We're pretty lucky.",
          context: "Perspective on their extraordinary journey."
        },
        social: {
          speaker1: "We should get the whole crew up here for this view.",
          speaker2: "Good idea, {name1}. Moments like this are better shared.",
          context: "Wanting to share beauty with everyone."
        }
      }
    }
  ]
};

// Backstory reveals triggered by high relationships
export const BACKSTORY_REVEALS = [
  // Social
  {
    relationshipThreshold: 70,
    personality: 'social',
    text: "You know, I never thought I'd end up in space. I was studying marine biology back on Earth.",
    context: "shares a piece of their past"
  },
  {
    relationshipThreshold: 85,
    personality: 'social',
    text: "I had a big family back home. Seven siblings. I think that's why I can't stand being alone out here.",
    context: "reveals the root of their sociability"
  },
  // Brave
  {
    relationshipThreshold: 80,
    personality: 'brave',
    text: "I lost my previous ship to pirates. That's why I'm so determined to protect this crew.",
    context: "reveals a painful memory"
  },
  {
    relationshipThreshold: 90,
    personality: 'brave',
    text: "People think I'm fearless. Truth is, I'm terrified every time. I just refuse to let it win.",
    context: "admits vulnerability behind the bravery"
  },
  // Technical
  {
    relationshipThreshold: 60,
    personality: 'technical',
    text: "My family has been engineers for five generations. This ship reminds me of my grandfather's workshop.",
    context: "talks about their heritage"
  },
  {
    relationshipThreshold: 80,
    personality: 'technical',
    text: "I built my first warp coil simulator when I was twelve. Blew out the power grid for three blocks.",
    context: "shares a childhood memory with a grin"
  },
  // Cautious
  {
    relationshipThreshold: 65,
    personality: 'cautious',
    text: "I've seen what happens when crews don't work together. That's why I always try to keep the peace.",
    context: "explains their cautious nature"
  },
  {
    relationshipThreshold: 85,
    personality: 'cautious',
    text: "My last posting... we lost half the crew because someone skipped a safety check. I won't let that happen again.",
    context: "reveals the trauma behind the caution"
  },
  // Optimistic
  {
    relationshipThreshold: 75,
    personality: 'optimistic',
    text: "My home colony was destroyed by a solar flare, but I still believe the universe has good things in store for us.",
    context: "shares their optimistic outlook despite tragedy"
  },
  {
    relationshipThreshold: 90,
    personality: 'optimistic',
    text: "I choose to be optimistic. Every day. It's not natural — it's a decision I make because the alternative nearly killed me.",
    context: "reveals that their optimism is hard-won"
  },
  // Pessimistic
  {
    relationshipThreshold: 70,
    personality: 'pessimistic',
    text: "I used to be different, you know. Then I spent two years stranded on a dead station waiting for rescue that almost didn't come.",
    context: "explains why they expect the worst"
  },
  {
    relationshipThreshold: 85,
    personality: 'pessimistic',
    text: "I'm not pessimistic. I'm prepared. There's a difference. ...at least that's what I tell myself.",
    context: "a moment of self-awareness"
  },
  // Introverted
  {
    relationshipThreshold: 75,
    personality: 'introverted',
    text: "I grew up on a research outpost. Population: four. I'm still getting used to... all of this.",
    context: "explains their quiet nature"
  },
  {
    relationshipThreshold: 90,
    personality: 'introverted',
    text: "You're one of the only people I've ever felt comfortable talking to. I just wanted you to know that.",
    context: "a rare and meaningful admission"
  },
  // Creative
  {
    relationshipThreshold: 65,
    personality: 'creative',
    text: "I was accepted to the Martian Academy of Arts before I enlisted. Sometimes I wonder what that life would have been like.",
    context: "shares a road not taken"
  },
  {
    relationshipThreshold: 85,
    personality: 'creative',
    text: "I keep a journal. Every system, every encounter. Someday I want to turn it all into something — a book, a symphony, I don't know yet.",
    context: "reveals a long-term dream"
  }
];

// System references for technical conversations
export const SYSTEM_REFERENCES = [
  'navigation array', 'life support', 'weapon systems', 'shield generators',
  'engine core', 'sensor array', 'communication hub', 'cargo bay'
];

/**
 * Generate a conversation based on trigger and crew personalities
 */
export function generateConversation(trigger, crew1, crew2, gameState = {}) {
  const templates = CONVERSATION_TEMPLATES[trigger];
  if (!templates || templates.length === 0) return null;

  const template = templates[Math.floor(Math.random() * templates.length)];
  
  // Find best personality match
  const personalities = ['social', 'technical', 'brave', 'cautious', 'optimistic', 'pessimistic', 'creative', 'introverted'];
  let bestMatch = 'social';
  let highestScore = 0;

  personalities.forEach(trait => {
    const score = (crew1.personality?.[trait] || 0) + (crew2.personality?.[trait] || 0);
    if (score > highestScore && template.templates[trait]) {
      highestScore = score;
      bestMatch = trait;
    }
  });

  const dialogue = template.templates[bestMatch] || template.templates.social;
  
  // Safety check - if no dialogue found, return null
  if (!dialogue || !dialogue.speaker1 || !dialogue.speaker2) {
    console.warn('[CrewConversations] No valid dialogue found for trigger:', trigger);
    return null;
  }
  
  return {
    id: template.id,
    speaker1: crew1.name,
    speaker2: crew2.name,
    dialogue: {
      line1: dialogue.speaker1.replace('{name1}', crew1.name).replace('{name2}', crew2.name)
        .replace('{system}', SYSTEM_REFERENCES[Math.floor(Math.random() * SYSTEM_REFERENCES.length)]),
      line2: dialogue.speaker2.replace('{name1}', crew1.name).replace('{name2}', crew2.name)
        .replace('{system}', SYSTEM_REFERENCES[Math.floor(Math.random() * SYSTEM_REFERENCES.length)]),
      context: dialogue.context
    },
    relationshipEffect: template.relationshipEffect,
    timestamp: Date.now()
  };
}

/**
 * Check if a backstory should be revealed
 */
export function checkBackstoryReveal(crew, relationship) {
  const reveals = BACKSTORY_REVEALS.filter(reveal => 
    relationship >= reveal.relationshipThreshold &&
    crew.personality?.[reveal.personality] > 60
  );
  
  if (reveals.length === 0) return null;
  
  const reveal = reveals[Math.floor(Math.random() * reveals.length)];
  return {
    speaker: crew.name,
    text: reveal.text,
    context: `${crew.name} ${reveal.context}`,
    timestamp: Date.now()
  };
}